import { useEffect, useState, useCallback } from "react";
import "../styles/index.less";
import { Col, Row, Button, Typography, Alert, Skeleton, Divider } from "antd";
import {
  RightCircleFilled,
  ReloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { get_active_news } from "../../../services/news";
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;

const MAX_NEWS = 4;

interface NewsItem {
  key: string;
  id: string;
  news_id: string;
  img: string;
  title: string;
  description?: string;
  created_date: string;
}

const formatDateThai = (dateString: any): string => {
  try {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  } catch {
    return "-";
  }
};

const SkeletonNews = ({
  showError,
  error,
  onRetry,
}: {
  showError?: boolean;
  error?: string | null;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="connews-wrapper">
      <div className="globalcontainer connews-container">
        {showError && (
          <Alert
            message={t("Error") || "เกิดข้อผิดพลาด"}
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: "16px" }}
            action={
              <Button
                size="small"
                danger
                icon={<ReloadOutlined />}
                onClick={onRetry}
              >
                {t("Retry") || "ลองใหม่"}
              </Button>
            }
          />
        )}
        <Row gutter={[16, 16]} style={{ width: "100%", margin: 0 }}>
          <Col xs={24} lg={15}>
            <Skeleton.Image
              active
              style={{
                width: "100%",
                height: "400px",
                borderRadius: "12px",
                display: "block",
              }}
            />
          </Col>
          <Col xs={24} lg={9}>
            <div className="connews-skeleton-side-list">
              {[0, 1, 2].map((i) => (
                <div key={i} className="connews-skeleton-side-item">
                  <Skeleton.Image
                    active
                    style={{
                      width: "72px",
                      height: "72px",
                      borderRadius: "8px",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <Skeleton active title={false} paragraph={{ rows: 2 }} />
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const EmptyNews = () => {
  const { t } = useTranslation();
  return (
    <div className="connews-wrapper">
      <div className="globalcontainer connews-container">
        <div className="task-section">
          <div className="section-title" data-aos="fade-up">
            <Divider
              className="dividline"
              style={{ backgroundColor: "#363636" }}
            />
          </div>
        </div>
        <div className="task-header">
          <span className="red-bar"></span>
          <p
            className="detail-title"
            style={{
              textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              marginBottom: "0px",
              fontFamily: "IBM Plex Sans Thai",
              padding: "0px",
              color: "#052240",
            }}
          >
            {t("News")}
          </p>
        </div>

        <div className="connews-empty-content">
          <InboxOutlined className="connews-empty-icon" />
          <Text className="connews-empty-text">
            {t("No news available") || "ไม่มีข่าว"}
          </Text>
        </div>
      </div>
      <div style={{ padding: "30px 40px 0px 40px" }}>
        <Divider className="dividline" style={{ backgroundColor: "#363636" }} />
      </div>
    </div>
  );
};

const Connews = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await get_active_news();
      setNewsData(Array.isArray(res?.result) ? res.result : []);
    } catch (e) {
      console.error("Error fetching news:", e);
      setError(
        t("Failed to load news. Please try again.") ||
          "ไม่สามารถโหลดข่าวได้ กรุณาลองใหม่อีกครั้ง",
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const getImageSrc = (news: NewsItem): string => {
    if (imageErrors.has(news.news_id)) return "";
    return news.img?.startsWith("/data/")
      ? `${process.env.REACT_APP_API}${news.img}`
      : news.img || "";
  };

  const handleImageError = (newsId: string) => {
    setImageErrors((prev) => new Set(prev).add(newsId));
  };

  const handleNavigate = (newsId: string) => {
    if (newsId && newsId !== "default") {
      window.scrollTo(0, 0);
      navigate(`/news/detail/${newsId}`);
    }
  };

  const displayNews = newsData.slice(0, MAX_NEWS);

  if (loading) return <SkeletonNews />;
  if (error)
    return <SkeletonNews showError error={error} onRetry={fetchNews} />;
  if (displayNews.length === 0) return <EmptyNews />;

  const [featuredNews, ...sideNews] = displayNews;
  const featuredImgSrc = getImageSrc(featuredNews);

  return (
    <div className="connews-wrapper">
      <div className="globalcontainer connews-container">
        <div className="task-section">
          <div className="section-title" data-aos="fade-up">
            <Divider
              className="dividline"
              style={{ backgroundColor: "#363636" }}
            />
          </div>
        </div>
        <div className="task-header">
          <span className="red-bar"></span>
          <p
            className="detail-title"
            style={{
              textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              marginBottom: "0px",
              fontFamily: "IBM Plex Sans Thai",
              padding: "0px",
              color: "#052240",
            }}
          >
            {t("News")}
          </p>
        </div>

        <Row gutter={[16, 16]} style={{ width: "100%", margin: 0 }}>
          {/* Featured (left) */}
          <Col xs={24} lg={15}>
            <div
              className="connews-featured"
              onClick={() => handleNavigate(featuredNews.news_id)}
            >
              {featuredImgSrc ? (
                <img
                  className="connews-featured-img"
                  src={featuredImgSrc}
                  alt={featuredNews.title}
                  onError={() => handleImageError(featuredNews.news_id)}
                  loading="lazy"
                />
              ) : (
                <div className="connews-featured-no-img" />
              )}
              <div className="connews-featured-overlay" />
              <div className="connews-featured-text-area">
                <Text className="connews-featured-date">
                  {formatDateThai(featuredNews.created_date)}
                </Text>
                <Title level={3} className="connews-featured-title">
                  {featuredNews.title || t("Untitled") || "ไม่มีหัวข้อ"}
                </Title>
                {featuredNews.description && (
                  <Paragraph className="connews-featured-desc">
                    {featuredNews.description}
                  </Paragraph>
                )}
              </div>
            </div>
          </Col>

          {/* Side news list (right) */}
          <Col xs={24} lg={9}>
            <div className="connews-side-list">
              {sideNews.length > 0 ? (
                <div className="connews-side-inner">
                  {sideNews.map((news) => {
                    const imgSrc = getImageSrc(news);
                    return (
                      <div
                        key={news.news_id}
                        className="connews-side-item"
                        onClick={() => handleNavigate(news.news_id)}
                      >
                        <div className="connews-side-thumb">
                          {imgSrc ? (
                            <img
                              className="connews-side-thumb-img"
                              src={imgSrc}
                              alt={news.title}
                              onError={() => handleImageError(news.news_id)}
                              loading="lazy"
                            />
                          ) : (
                            <div className="connews-side-thumb-placeholder" />
                          )}
                        </div>
                        <div className="connews-side-text">
                          <Text strong className="connews-side-title-text">
                            {news.title || t("Untitled") || "ไม่มีหัวข้อ"}
                          </Text>
                          <Text type="secondary" className="connews-side-date">
                            {formatDateThai(news.created_date)}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="connews-side-empty">
                  <Text
                    type="secondary"
                    style={{ fontFamily: "IBM Plex Sans Thai" }}
                  >
                    {t("No more news") || "ไม่มีข่าวเพิ่มเติม"}
                  </Text>
                </div>
              )}

              <Button
                type="primary"
                size="large"
                icon={<RightCircleFilled />}
                className="connews-more-btn"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/news");
                }}
              >
                {t("More News") || "ข่าวทั้งหมด"}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
      <div style={{ padding: "30px 40px 0px 40px" }}>
        <Divider className="dividline" style={{ backgroundColor: "#363636" }} />
      </div>
    </div>
  );
};

export default Connews;
